import { useAlertContext } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { UpdateAppointmentExchangeRequest, appointmentExchangesApi } from "@/api";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

interface OfferParams {
  exchangeId: string;
  offerId: string;
}

interface UpdateRequestParams {
  exchangeId: string;
  request: UpdateAppointmentExchangeRequest;
}

/**
 * The requester's decisions on an own request (Anfrage): accept or reject an offer, edit or delete the
 * request. All refresh the exchanges prefix (list, badge, overlay, exchangeable appointments); accepting
 * also refreshes the appointments, because the swap moves two appointments (Termine) to other calendars
 * (Kalender). A failure refreshes too, so a row that went stale (e.g. a 409 because the offer was
 * withdrawn meanwhile) shows its real state.
 */
export function useExchangeRequestMutations() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showSuccess } = useAlertContext();

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: [QueryKey.AppointmentExchanges] });
  const invalidateAppointments = () => void queryClient.invalidateQueries({ queryKey: [QueryKey.Appointments] });

  const acceptOffer = useMutation({
    mutationFn: ({ exchangeId, offerId }: OfferParams) =>
      appointmentExchangesApi.acceptAppointmentExchangeOffer({ id: exchangeId, offerId }),
    onSuccess: () => {
      invalidate();
      invalidateAppointments();
      showSuccess(t("exchange.offersDialog.acceptSuccessMessage"));
    },
    onError: invalidate,
  });

  const rejectOffer = useMutation({
    mutationFn: ({ exchangeId, offerId }: OfferParams) =>
      appointmentExchangesApi.rejectAppointmentExchangeOffer({ id: exchangeId, offerId }),
    onSuccess: () => {
      invalidate();
      showSuccess(t("exchange.offersDialog.rejectSuccessMessage"));
    },
    onError: invalidate,
  });

  const updateRequest = useMutation({
    mutationFn: ({ exchangeId, request }: UpdateRequestParams) =>
      appointmentExchangesApi.updateAppointmentExchange({
        id: exchangeId,
        updateAppointmentExchangeRequest: request,
      }),
    onSuccess: () => {
      invalidate();
      showSuccess(t("exchange.editRequestDialog.successMessage"));
    },
    onError: invalidate,
  });

  const deleteRequest = useMutation({
    mutationFn: (exchangeId: string) => appointmentExchangesApi.deleteAppointmentExchange({ id: exchangeId }),
    onSuccess: () => {
      invalidate();
      showSuccess(t("exchange.deleteRequestDialog.successMessage"));
    },
    onError: invalidate,
  });

  return {
    acceptOffer,
    rejectOffer,
    updateRequest,
    deleteRequest,
    isBusy: acceptOffer.isPending || rejectOffer.isPending || updateRequest.isPending || deleteRequest.isPending,
  };
}
